import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Formik, Form, useFormikContext } from "formik";
import { useRequest } from "redux-query-react";
import {
  MultiFieldLine,
  MultiSelectField,
  SelectField,
  TextField,
  CreatableInputOnly,
  ParagraphTextField,
  DatepickerField,
  AsyncMultiSelectField,
} from "./FormFields";
import { Button, InvertedButton } from "./Button";
import { validateMetadata } from "../utils/validators";
import { useSaveRecord } from "../hooks/messagesAndRequests";
import { metadataFieldInfo, metadataInit } from "../constants/metadataFields";
import { useNavigate } from "react-router-dom";
import { GREY_DARK, MAIN_CONTENT_WIDTH } from "../constants/cssVars";
import {
  countyOptions,
  rbfsRegionsOptions,
  cityOptions,
  modelTypeOptions,
  modelCategoryOptions,
} from "../constants/formOptions";
import { Footer } from "./Common";
import { useSelectedRecord } from "../hooks/useSelectedRecord";

import { capitalizeFirstLetter } from "../utils/helpers";

import { useConvertedContactFields } from "./Contacts";

import {
  getRecordQuery,
  getPrePopulateQuery,
  searchBaselineModelsQuery,
} from "../actions/queries";
import { convertDbPropertiesToAppFields } from "../utils/fieldConversion";
import { toast } from "react-toastify";
import { WaveLoader } from "./Loaders";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { getProjectNameForRoles, isNctcogUser } from "../utils/userRoles";
import { decodeStoredModelType } from "../utils/modelTypeValue";
import { BackButton, BackIcon } from "./Settings";

const Content = styled.div`
  width: ${MAIN_CONTENT_WIDTH}px;
  @media only screen and (max-width: ${MAIN_CONTENT_WIDTH}px) {
    width: 100%;
  }
`;

const IntroText = styled.p`
  color: ${GREY_DARK};
  font-size: 0.95em;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 4px;
`;

const FormWrapper = styled.div`
  position: relative;
`;

const InfoDatepickerField = (props) => {
  return (
    <DatepickerField
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

const InfoMultiTextInputCreateOnly = (props) => {
  return (
    <CreatableInputOnly
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

const InfoTextField = (props) => {
  return (
    <TextField
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

const InfoParagraphTextField = (props) => {
  return (
    <ParagraphTextField
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

const InfoSelectField = (props) => {
  return (
    <SelectField
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

const InfoAsyncMultiSelectField = (props) => {
  return (
    <AsyncMultiSelectField
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

export const getContactName = (name) => {
  if (name.includes("contacts.")) {
    const contactArray = name.split(".");
    const contactString = `contact${capitalizeFirstLetter(contactArray[2])}`;
    return contactString;
  }
  return name;
};

const InfoMultiSelectField = (props) => {
  return (
    <MultiSelectField
      {...props}
      label={`${getFriendlyName(props.name)}${
        getRequired(props.name) ? "*" : ""
      }`}
      info={getDescription(props.name)}
    />
  );
};

const getDescription = (name) =>
  metadataFieldInfo[getContactName(name)].description;
const getFriendlyName = (name) =>
  metadataFieldInfo[getContactName(name)].friendlyName;
const getRequired = (name) => metadataFieldInfo[getContactName(name)].required;

const textFromList = (value) =>
  Array.isArray(value) ? value.join(", ") : value;

const splitCommaSeparated = (value) =>
  typeof value === "string"
    ? value.split(",").map((item) => item.trim())
    : value;

const normalizeBaselineModel = (baselineModel) => {
  if (Array.isArray(baselineModel)) {
    return baselineModel;
  }
  return baselineModel ? [baselineModel] : [];
};

const prepareInitialMetadata = (selectedRecord) => {
  const initValues = {
    ...metadataInit,
    ...(selectedRecord ? selectedRecord.currentMetadata : {}),
  };

  if (selectedRecord) {
    const decodedModelType = decodeStoredModelType(
      initValues.modelType,
      initValues.otherModelType,
    );
    initValues.modelType = decodedModelType.modelType;
    initValues.otherModelType = decodedModelType.otherModelType;
  }

  initValues.county = splitCommaSeparated(initValues.county);
  initValues.city = splitCommaSeparated(initValues.city);
  initValues.modelInput = textFromList(initValues.modelInput);
  initValues.modelOutput = textFromList(initValues.modelOutput);
  initValues.baselineModel = normalizeBaselineModel(initValues.baselineModel);
  return initValues;
};

const isRealBaselineModel = (value) =>
  typeof value === "object" && Boolean(value?.tdisIdentifier);

const duplicateRealBaselineName = (value, seen) => {
  if (!isRealBaselineModel(value)) {
    return null;
  }
  if (seen.has(value.tdisIdentifier)) {
    return value.title || value.tdisIdentifier;
  }
  seen.add(value.tdisIdentifier);
  return null;
};

const findDuplicateBaseline = (selectedValues) => {
  const seenTdisIdentifiers = new Set();
  let seenExternalModel = false;

  for (const value of selectedValues) {
    if (value === "External Model" && seenExternalModel) {
      return { name: "External Model" };
    }
    if (value === "External Model") {
      seenExternalModel = true;
    }
    const duplicateName = duplicateRealBaselineName(value, seenTdisIdentifiers);
    if (duplicateName != null) {
      return { name: duplicateName };
    }
  }
  return null;
};

const hasMixedBaselineSelection = (selectedValues) =>
  selectedValues.includes("External Model") &&
  selectedValues.some(isRealBaselineModel);

const firstRealBaselineModel = (models) => {
  const list = Array.isArray(models) ? models : [];
  return list.find(isRealBaselineModel) || null;
};

const resetMetadataFields = (
  setFieldValue,
  { modelCategory = "", projectName = "" } = {},
) => {
  Object.keys(metadataInit).forEach((key) => {
    if (key !== "modelCategory") {
      setFieldValue(key, metadataInit[key]);
    }
  });
  if (modelCategory) {
    setFieldValue("modelCategory", modelCategory);
  }
  if (projectName) {
    setFieldValue("projectName", projectName);
  }
};

// Component to populate form fields from baseline model metadata
// The API only returns necessary fields, so we can populate all fields directly
const BaselineFieldPopulator = ({
  baselineMetadata,
  recordId,
  onPopulated,
}) => {
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    if (baselineMetadata && recordId === "new") {
      // Populate all fields from the response (API only returns necessary fields)
      Object.keys(baselineMetadata).forEach((key) => {
        let value = baselineMetadata[key];

        // Skip null/undefined values
        if (value === null || value === undefined) {
          return;
        }

        if (key === "projectName") {
          return;
        }

        // Handle array fields (county, city)
        if (key === "county" || key === "city") {
          if (typeof value === "string") {
            value = value.split(",").map((item) => item.trim());
          }
        }

        // Handle array fields that should be strings (modelInput, modelOutput)
        if (key === "modelInput" || key === "modelOutput") {
          if (Array.isArray(value)) {
            // Convert array to comma-separated string (even if empty)
            value = value.length > 0 ? value.join(", ") : "";
          } else if (typeof value !== "string") {
            // Not array or string, skip
            return;
          }
          // Always set modelInput/modelOutput, even if empty
          setFieldValue(key, value);
          return;
        }

        setFieldValue(key, value);
      });

      // Notify parent that population is complete
      onPopulated && onPopulated();
    }
  }, [baselineMetadata, recordId, setFieldValue, onPopulated]);

  return null;
};

export const MetadataForm = () => {
  const navigate = useNavigate();
  const [selectedRecord] = useSelectedRecord();
  return (
    <Content>
      <BackButton
        style={{ paddingTop: "20px" }}
        onClick={() => navigate("/app")}
        ariaLabel="back"
      >
        <BackIcon alt="back arrow" />
      </BackButton>
      <h2 style={{ marginTop: "10px" }}>
        {selectedRecord ? "Edit" : "Create new"} metadata record
      </h2>
      <IntroText>
        This metadata record should refer to a <b>single</b> model. We will use
        this metadata record to generate a metadata YAML file that will be
        included in your model's upload.
      </IntroText>
      <h3>Basic</h3>
      <MetadataFormContent />
    </Content>
  );
};

const MetadataFormContent = () => {
  const navigate = useNavigate();
  const [selectedRecord, recordId] = useSelectedRecord();
  // For saving via "Generate Metadata", we want to show loading on the "Generate Metadata" button & hide the toast notification
  const [saveObjViaMetadataClick, saveObjViaMetadataClickSubmitting] =
    useSaveRecord(recordId, true);
  const currentUser = useCurrentUser();
  const { accessToken } = currentUser;
  const isNctcog = isNctcogUser(currentUser.roles);
  const projectNameForUser = getProjectNameForRoles(currentUser.roles);
  const showProjectName = projectNameForUser !== "";
  const showRegion = !isNctcog;
  const preservedProjectName = showProjectName ? projectNameForUser : "";
  const [{ isFinished: getRequestFinished }] = useRequest(
    accessToken && recordId !== "new"
      ? getRecordQuery(recordId, accessToken)
      : null,
  );
  const [pressedGenerate, setPressedGenerate] = useState(false);
  const [baselineTdisIdentifier, setBaselineTdisIdentifier] = useState(null);
  const [isLoadingBaselineMetadata, setIsLoadingBaselineMetadata] =
    useState(false);
  const initValues = prepareInitialMetadata(selectedRecord);

  const dbContactsField = useConvertedContactFields(recordId);

  // Store baseline metadata to populate when Formik is ready
  const [baselineMetadataToPopulate, setBaselineMetadataToPopulate] =
    useState(null);

  // Track which baseline record we've already processed to prevent duplicate toasts
  const processedBaselineTdisIdentifier = useRef(null);
  // Track the active baseline fetch to ignore stale responses
  const activeBaselineRequestId = useRef(null);

  // Fetch baseline metadata when baselineTdisIdentifier is set
  useEffect(() => {
    if (baselineTdisIdentifier && recordId === "new" && accessToken) {
      // Only fetch if we haven't already processed this baseline record
      if (processedBaselineTdisIdentifier.current !== baselineTdisIdentifier) {
        const requestId = `${baselineTdisIdentifier}-${Date.now()}`;
        activeBaselineRequestId.current = requestId;
        setIsLoadingBaselineMetadata(true);
        toast.info("Loading baseline model metadata...", {
          toastId: "baseline-loading",
          autoClose: false,
        });

        const query = getPrePopulateQuery(baselineTdisIdentifier, accessToken);

        fetch(query.url, query.options)
          .then(async (response) => {
            // Ignore if a newer request was started
            if (activeBaselineRequestId.current !== requestId) {
              return null;
            }
            if (!response.ok) {
              if (response.status === 401) {
                toast.error("Session expired. Please login again.");
                toast.dismiss("baseline-loading");
                return null;
              }
              throw new Error(`API Error: ${response.status}`);
            }
            return response.json();
          })
          .then((data) => {
            // Ignore stale responses
            if (activeBaselineRequestId.current !== requestId) {
              return;
            }
            if (data) {
              // Transform database fields to app fields
              const transformedData = convertDbPropertiesToAppFields(data);
              setBaselineMetadataToPopulate(transformedData.currentMetadata);
              processedBaselineTdisIdentifier.current = baselineTdisIdentifier;
              toast.dismiss("baseline-loading");
              toast.success("Baseline model metadata loaded successfully");
            }
          })
          .catch((error) => {
            if (activeBaselineRequestId.current !== requestId) {
              return;
            }
            console.error("Error fetching baseline pre-populate data:", error);
            toast.dismiss("baseline-loading");
            toast.error("Failed to load baseline model metadata");
          })
          .finally(() => {
            if (activeBaselineRequestId.current === requestId) {
              setIsLoadingBaselineMetadata(false);
            }
          });
      }
    }
  }, [baselineTdisIdentifier, recordId, accessToken]);

  // Reset processed baseline identifier when baselineTdisIdentifier changes or is cleared
  useEffect(() => {
    if (!baselineTdisIdentifier) {
      processedBaselineTdisIdentifier.current = null;
      activeBaselineRequestId.current = null;
      toast.dismiss("baseline-loading");
      setIsLoadingBaselineMetadata(false);
    }
  }, [baselineTdisIdentifier]);

  // Load options for baseline model search
  const loadBaselineOptions = useCallback(
    async (inputValue) => {
      if (!inputValue || inputValue.trim().length < 4) {
        return [];
      }

      try {
        const query = searchBaselineModelsQuery(accessToken, inputValue);
        if (!query) return [];

        const response = await fetch(query.url, query.options);

        if (!response.ok) {
          if (response.status === 401) {
            toast.error("Session expired. Please login again.");
            return [];
          }
          throw new Error(`API Error: ${response.status}`);
        }

        const data = await response.json();

        // Transform API response to react-select format
        // Store both tdisIdentifier and title (need tdisIdentifier for API call, title for display/backend)
        // Store full item for formatOptionLabel to display HUC and County in dropdown
        const apiResults = data.map((item) => ({
          label: item.title || `${item.county || item.huc || "Unknown"}`,
          value: {
            tdisIdentifier: item.tdisIdentifier,
            title: item.title || `${item.county || item.huc || "Unknown"}`,
          },
          item: item, // Store full item for formatOptionLabel to access HUC and County
        }));

        return apiResults;
      } catch (error) {
        console.error("Error searching baseline models:", error);
        toast.error("Failed to search baseline models");
        return [];
      }
    },
    [accessToken],
  );

  // Convert stored value back to option format
  const getBaselineOptionFromValue = useCallback((value) => {
    if (!value) return null;

    // Handle array of values (multi-select)
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          // Handle "External Model" string
          if (item === "External Model") {
            return {
              label: "External Model",
              value: "External Model",
            };
          }

          // Handle object format (baseline model with tdisIdentifier)
          if (typeof item === "object" && item.title) {
            return {
              label: item.title,
              value: item,
            };
          }

          // Legacy string format (for backward compatibility)
          if (typeof item === "string") {
            return {
              label: item,
              value: { tdisIdentifier: "", title: item },
            };
          }

          return null;
        })
        .filter(Boolean);
    }

    // Handle single value (for backward compatibility)
    // Handle "External Model" string
    if (value === "External Model") {
      return {
        label: "External Model",
        value: "External Model",
      };
    }

    // Handle object format (baseline model with tdisIdentifier)
    if (typeof value === "object" && value.title) {
      return {
        label: value.title,
        value: value,
      };
    }

    // Legacy string format (for backward compatibility)
    if (typeof value === "string") {
      return {
        label: value,
        value: { tdisIdentifier: "", title: value },
      };
    }

    return null;
  }, []);

  function onKeyDown(keyEvent) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  }

  const noErrors = (errors) => {
    if (Object.keys(errors).length === 0) {
      return true;
    }
    const totalErrors = Object.keys(errors).length;
    toast.error(
      `Please fix ${totalErrors} error${totalErrors > 1 ? "s" : ""}.`,
    );
    return false;
  };

  const attachCurrentTime = (dateStr) => {
    if (!dateStr) return dateStr;
    // If time already present, keep as-is
    if (dateStr.includes("T")) return dateStr;
    const now = new Date();
    const pad = (num) => String(num).padStart(2, "0");
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds(),
    )}`;
    return `${dateStr}T${time}`;
  };

  const handleGenerateMetadataClick = (values, errors) => {
    if (noErrors(errors)) {
      // Convert baselineModel from objects to array of titles (convertAppFieldsToDbProperties will convert to comma-separated string)
      const baselineModelTitles = Array.isArray(values.baselineModel)
        ? values.baselineModel.map((model) => {
            if (model === "External Model") {
              return "External Model";
            }
            if (typeof model === "object" && model.title) {
              return model.title;
            }
            return model;
          })
        : [];

      const payload = {
        ...values,
        ...(showProjectName ? { projectName: projectNameForUser } : {}),
        ...(isNctcog ? { rbfsRegionID: "" } : {}),
        baselineModel: baselineModelTitles,
        createdDate: attachCurrentTime(values.createdDate),
        updatedDate: values.updatedDate
          ? attachCurrentTime(values.updatedDate)
          : "",
      };
      saveObjViaMetadataClick(
        { ...payload, contacts: dbContactsField },
        (newId) =>
          navigate(`/app/instructions/${selectedRecord ? recordId : newId}`),
      );
    }
  };

  if (recordId !== "new" && !getRequestFinished) {
    return <WaveLoader />;
  }

  return (
    <Formik
      validate={validateMetadata(metadataFieldInfo, {
        isNctcog,
        isRbfs: showProjectName && !isNctcog,
      })}
      enableReinitialize={true}
      onSubmit={() => null}
      initialValues={{
        ...initValues,
        ...(showProjectName ? { projectName: projectNameForUser } : {}),
      }}
    >
      {({ errors, touched, submitCount, values, setFieldValue }) => {
        // return error if field is touched
        const getError = (name) =>
          (touched[name] || submitCount >= 1 || pressedGenerate) &&
          errors[name];

        // Wrapper for loadBaselineOptions that handles mutual exclusivity
        // Regular function (not useCallback) since it's inside render and needs current values
        const loadBaselineOptionsWithFilter = async (inputValue) => {
          const currentBaselineModels = values.baselineModel || [];
          const hasExternalModel =
            currentBaselineModels.includes("External Model");
          const hasRealModels = currentBaselineModels.some(
            (model) => typeof model === "object" && model.tdisIdentifier,
          );

          // Get base options
          const options = await loadBaselineOptions(inputValue);

          // Apply mutual exclusivity filters
          let filteredOptions = options;

          // If External Model is selected, filter out all real models
          if (hasExternalModel) {
            filteredOptions = [];
            // Only show External Model if input matches
            if (inputValue.toLowerCase().startsWith("ext")) {
              filteredOptions.push({
                label: "External Model",
                value: "External Model",
                item: null,
              });
            }
          } else if (hasRealModels) {
            // If real models are selected, filter out External Model
            filteredOptions = options.filter(
              (opt) => opt.value !== "External Model",
            );
            // Add External Model option if input matches, but only if no real models exist
            if (
              inputValue.toLowerCase().startsWith("ext") &&
              currentBaselineModels.length === 0
            ) {
              filteredOptions.push({
                label: "External Model",
                value: "External Model",
                item: null,
              });
            }
          } else {
            // No selections yet - show both types
            if (inputValue.toLowerCase().startsWith("ext")) {
              filteredOptions = [
                ...options,
                {
                  label: "External Model",
                  value: "External Model",
                  item: null,
                },
              ];
            }
          }

          return filteredOptions;
        };

        // Handle modelCategory change - clear baselineModel if not "Alternative Analysis Model"
        const clearPopulatedBaselineFields = (modelCategory = "") => {
          resetMetadataFields(setFieldValue, {
            modelCategory,
            projectName: preservedProjectName,
          });
        };

        const updateBaselineFetch = (selectedValues) => {
          const firstRealModel = firstRealBaselineModel(selectedValues);
          const previousFirstRealModel = firstRealBaselineModel(
            values.baselineModel,
          );
          const sameRealModel =
            previousFirstRealModel &&
            firstRealModel &&
            previousFirstRealModel.tdisIdentifier ===
              firstRealModel.tdisIdentifier;

          if (firstRealModel && recordId === "new") {
            if (!sameRealModel) {
              setBaselineTdisIdentifier(firstRealModel.tdisIdentifier);
            }
            return;
          }

          setBaselineTdisIdentifier(null);
          setBaselineMetadataToPopulate(null);
          if (recordId === "new" && previousFirstRealModel) {
            clearPopulatedBaselineFields(values.modelCategory);
          }
        };

        const handleModelCategoryChange = (newValue) => {
          setFieldValue("modelCategory", newValue);
          const currentBaselineModels = values.baselineModel || [];
          if (
            newValue !== "Alternative Analysis Model" &&
            currentBaselineModels.length > 0
          ) {
            setFieldValue("baselineModel", []);
            setBaselineTdisIdentifier(null);
            setBaselineMetadataToPopulate(null);

            if (recordId === "new") {
              clearPopulatedBaselineFields();
            }
          }
        };

        const handleBaselineModelChange = (selectedOptions) => {
          if (!selectedOptions || selectedOptions.length === 0) {
            setFieldValue("baselineModel", []);
            setBaselineTdisIdentifier(null);
            setBaselineMetadataToPopulate(null);
            if (recordId === "new") {
              clearPopulatedBaselineFields(values.modelCategory);
            }
            return;
          }

          const selectedValues = selectedOptions.map((opt) => opt.value);
          const duplicate = findDuplicateBaseline(selectedValues);
          if (duplicate) {
            toast.error(
              `"${duplicate.name}" is already selected. Duplicate models are not allowed.`,
            );
            return;
          }
          if (hasMixedBaselineSelection(selectedValues)) {
            toast.error(
              "External Model cannot be selected with other baseline models",
            );
            return;
          }

          setFieldValue("baselineModel", selectedValues);
          updateBaselineFetch(selectedValues);
        };

        const showBaselineModel =
          values.modelCategory === "Alternative Analysis Model";
        const showOtherModelType = isNctcog && values.modelType === "Other";

        const handleModelTypeChange = (newValue) => {
          setFieldValue("modelType", newValue);
          if (newValue !== "Other") {
            setFieldValue("otherModelType", "");
          }
        };

        return (
          <>
            <BaselineFieldPopulator
              baselineMetadata={baselineMetadataToPopulate}
              recordId={recordId}
              onPopulated={() => setBaselineMetadataToPopulate(null)}
            />
            <FormWrapper>
              {isLoadingBaselineMetadata && (
                <LoadingOverlay>
                  <WaveLoader />
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "0.9em",
                      color: GREY_DARK,
                    }}
                  >
                    Loading baseline model metadata...
                  </span>
                </LoadingOverlay>
              )}
              <Form onKeyDown={onKeyDown}>
                {showProjectName && (
                  <MultiFieldLine columns="50% 50%">
                    <InfoTextField
                      name="projectName"
                      error={getError("projectName")}
                      disabled
                    />
                  </MultiFieldLine>
                )}
                <MultiFieldLine columns="50% 50%">
                  <InfoSelectField
                    name="modelCategory"
                    options={modelCategoryOptions}
                    error={getError("modelCategory")}
                    onChange={handleModelCategoryChange}
                  />
                  {showBaselineModel && (
                    <InfoAsyncMultiSelectField
                      name="baselineModel"
                      loadOptions={loadBaselineOptionsWithFilter}
                      optionSelector={getBaselineOptionFromValue}
                      isOptionDisabled={(option) => {
                        // Check if this option is already selected
                        const currentBaselineModels =
                          values.baselineModel || [];
                        if (!option || !option.value) return false;

                        // Check for External Model
                        if (option.value === "External Model") {
                          return currentBaselineModels.includes(
                            "External Model",
                          );
                        }

                        // Check for real models by comparing tdisIdentifier
                        if (
                          typeof option.value === "object" &&
                          option.value.tdisIdentifier
                        ) {
                          return currentBaselineModels.some((selected) => {
                            if (
                              typeof selected === "object" &&
                              selected.tdisIdentifier
                            ) {
                              return (
                                selected.tdisIdentifier ===
                                option.value.tdisIdentifier
                              );
                            }
                            return false;
                          });
                        }

                        return false;
                      }}
                      // placeholder="Type HUC or County"
                      customPlaceholder={
                        <span>
                          Type HUC or County or <em>"External Model"</em>
                        </span>
                      }
                      error={getError("baselineModel")}
                      onChange={handleBaselineModelChange}
                    />
                  )}
                </MultiFieldLine>
                <MultiFieldLine columns="50% 50%">
                  {showRegion && (
                    <InfoSelectField
                      name="rbfsRegionID"
                      options={rbfsRegionsOptions}
                      error={getError("rbfsRegionID")}
                    />
                  )}
                  <InfoTextField
                    name="hydrologicUnitCode"
                    error={getError("hydrologicUnitCode")}
                  />
                </MultiFieldLine>

                <MultiFieldLine columns="50% 50%">
                  <InfoMultiSelectField
                    name="county"
                    options={countyOptions}
                    error={getError("county")}
                  />
                  <InfoMultiSelectField
                    name="city"
                    options={cityOptions}
                    error={getError("city")}
                  />
                </MultiFieldLine>

                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="modelName"
                    error={getError("modelName")}
                  />
                  <InfoTextField name="creator" error={getError("creator")} />
                </MultiFieldLine>
                <MultiFieldLine columns="100%">
                  <InfoParagraphTextField
                    name="modelDescription"
                    error={getError("modelDescription")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="100%">
                  <InfoParagraphTextField
                    name="modelPurpose"
                    error={getError("modelPurpose")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="100%">
                  <InfoParagraphTextField
                    name="kewyWordTerm"
                    error={getError("kewyWordTerm")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="updateFrequency"
                    error={getError("updateFrequency")}
                  />
                  <InfoTextField
                    name="useConstraint"
                    error={getError("useConstraint")}
                  />
                </MultiFieldLine>

                <h3>Model details</h3>
                <MultiFieldLine columns="50% 50%">
                  <InfoSelectField
                    name="modelType"
                    options={modelTypeOptions}
                    error={getError("modelType")}
                    onChange={handleModelTypeChange}
                  />
                  {showOtherModelType && (
                    <InfoTextField
                      name="otherModelType"
                      error={getError("otherModelType")}
                    />
                  )}
                </MultiFieldLine>

                <MultiFieldLine columns="100%">
                  <InfoMultiTextInputCreateOnly
                    name="modelInput"
                    error={getError("modelInput")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="100%">
                  <InfoMultiTextInputCreateOnly
                    name="modelOutput"
                    error={getError("modelOutput")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="softwareName"
                    error={getError("softwareName")}
                  />
                  <InfoTextField
                    name="softwareVersion"
                    error={getError("softwareVersion")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="50% 50%">
                  <InfoDatepickerField
                    name="createdDate"
                    error={getError("createdDate")}
                  />
                  <InfoDatepickerField
                    name="updatedDate"
                    error={getError("updatedDate")}
                  />
                </MultiFieldLine>

                <h3 style={{ marginTop: "40px" }}>Model Contacts</h3>
                <p
                  style={{
                    fontSize: "0.95em",
                    color: GREY_DARK,
                    margin: "0px 0px 20px 0px",
                  }}
                >
                  The following contact fields will be used as the primary
                  point(s) of contact for this model. At least one contact is
                  required.
                </p>

                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="contactName"
                    error={getError("contactName")}
                  />
                  <InfoTextField
                    name="contactAffiliation"
                    error={getError("contactAffiliation")}
                  />
                </MultiFieldLine>

                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="contactEmail"
                    error={getError("contactEmail")}
                  />
                  <InfoTextField
                    name="contactPhoneNumber"
                    error={getError("contactPhoneNumber")}
                  />
                </MultiFieldLine>
                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="contactDepartment"
                    error={getError("contactDepartment")}
                  />
                </MultiFieldLine>
                <h3 style={{ marginTop: "40px" }}>Spatial Information</h3>
                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="spatialDataType"
                    error={getError("spatialDataType")}
                  />
                  <InfoTextField
                    name="spatialExtentDecimalDegrees"
                    error={getError("spatialExtentDecimalDegrees")}
                  />
                </MultiFieldLine>

                <MultiFieldLine columns="50% 50%">
                  <InfoTextField
                    name="boundaryName"
                    error={getError("boundaryName")}
                  />
                  <InfoTextField
                    name="horizantalCoordinateSystem"
                    error={getError("horizantalCoordinateSystem")}
                  />
                </MultiFieldLine>
                <div style={{ width: "100%", height: "120px" }}></div>
                <Footer>
                  <div>
                    <div>
                      <InvertedButton onClick={() => navigate("/app/")}>
                        Back
                      </InvertedButton>
                      <div>
                        <Button
                          short={true}
                          type="button"
                          isLoading={saveObjViaMetadataClickSubmitting}
                          onClick={() => {
                            !pressedGenerate && setPressedGenerate(true);
                            handleGenerateMetadataClick(values, errors);
                          }}
                        >
                          Save & Proceed to Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                </Footer>
              </Form>
            </FormWrapper>
          </>
        );
      }}
    </Formik>
  );
};
